import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Calendar, TrendingUp, Award, ChevronDown, ChevronUp, Edit2, RotateCcw, Paperclip, Eye, FileText, Image as ImageIcon } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

const ParentGradesPage = () => {
  const [children, setChildren] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [completedWork, setCompletedWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);
  const [expandedWork, setExpandedWork] = useState(null);
  const [isEditingGrade, setIsEditingGrade] = useState(false);
  const [isSendingBack, setIsSendingBack] = useState(false);
  const [editGradeData, setEditGradeData] = useState({
    pointsAwarded: '',
    notes: '',
    tokens: 0
  });
  const [sendBackNotes, setSendBackNotes] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [allAssignments, setAllAssignments] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadCompletedWork();
      loadAllAssignments();
    }
  }, [selectedChild]);

  const loadData = async () => {
    try {
      const [childrenData, subjectsData] = await Promise.all([
        api.getChildren(),
        api.getSubjects()
      ]);
      setChildren(childrenData);
      setSubjects(subjectsData);
      if (childrenData.length > 0) {
        setSelectedChild(childrenData[0].id);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllAssignments = async () => {
    try {
      const data = await api.getAssignments(selectedChild);
      setAllAssignments(data);
    } catch (error) {
      console.error('Failed to load assignments:', error);
    }
  };

  const loadCompletedWork = async () => {
    try {
      const data = await api.getCompletedWork(selectedChild);
      setCompletedWork(data);
    } catch (error) {
      console.error('Failed to load completed work:', error);
      toast({
        title: 'Error',
        description: 'Failed to load grades',
        variant: 'destructive'
      });
    }
  };

  const getGradeColor = (percentage) => {
    if (percentage >= 90) return 'bg-green-100 text-green-700 border-green-300';
    if (percentage >= 80) return 'bg-blue-100 text-blue-700 border-blue-300';
    if (percentage >= 70) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    if (percentage >= 60) return 'bg-orange-100 text-orange-700 border-orange-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };

  const handleExpandWork = (work) => {
    if (expandedWork?.id === work.id) {
      setExpandedWork(null);
    } else {
      setExpandedWork(work);
    }
  };

  const handleEditGrade = (work) => {
    setExpandedWork(work);
    setEditGradeData({
      pointsAwarded: work.points_awarded.toString(),
      notes: work.notes || '',
      tokens: work.tokens_awarded || 0
    });
    setIsEditingGrade(true);
  };

  const handleUpdateGrade = async () => {
    try {
      const pointsAwarded = parseFloat(editGradeData.pointsAwarded);
      const pointsPossible = parseFloat(expandedWork.points_possible);

      await api.updateGrade(expandedWork.id, {
        assignment_id: expandedWork.id,
        points_awarded: pointsAwarded,
        notes: editGradeData.notes,
        tokens_awarded: parseInt(editGradeData.tokens)
      });

      const percentage = pointsPossible > 0 ? (pointsAwarded / pointsPossible) * 100 : 100;
      const bonusPoints = pointsAwarded > pointsPossible ? pointsAwarded - pointsPossible : 0;
      
      const description = bonusPoints > 0 
        ? `${expandedWork.title} has been regraded to ${pointsAwarded}/${pointsPossible} (${Math.round(percentage)}%) with ${bonusPoints} bonus points`
        : `${expandedWork.title} has been regraded to ${pointsAwarded}/${pointsPossible} (${Math.round(percentage)}%)`;

      toast({
        title: 'Grade Updated!',
        description
      });

      await loadCompletedWork();
      setIsEditingGrade(false);
      setExpandedWork(null);
    } catch (error) {
      console.error('Failed to update grade:', error);
      toast({
        title: 'Error',
        description: 'Failed to update grade',
        variant: 'destructive'
      });
    }
  };

  const handleSendBack = (work) => {
    setExpandedWork(work);
    setSendBackNotes('');
    setIsSendingBack(true);
  };

  const handleSendBackConfirm = async () => {
    try {
      await api.sendBackAssignment(expandedWork.id, sendBackNotes);

      toast({
        title: 'Assignment Sent Back',
        description: `${expandedWork.title} has been sent back to the student for resubmission.`
      });

      await loadCompletedWork();
      setIsSendingBack(false);
      setExpandedWork(null);
      setSendBackNotes('');
    } catch (error) {
      console.error('Failed to send back assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to send back assignment',
        variant: 'destructive'
      });
    }
  };

  // Group work by subject - need to consolidate by actual subject (not by subject_id)
  // because subject_ids might vary for replaced subjects
  // Get all assignments that are due up to now
  const now = new Date();
  const dueAssignments = allAssignments.filter(assignment => {
    const dueDate = new Date(assignment.due_date);
    return dueDate <= now;
  });

  // Group assignments by subject
  const assignmentsBySubject = dueAssignments.reduce((acc, assignment) => {
    const subject = subjects.find(s => 
      String(s.id) === String(assignment.subject_id) || 
      (s.replaces_default_id && String(s.replaces_default_id) === String(assignment.subject_id))
    );
    
    if (subject) {
      const key = subject.id;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(assignment);
    }
    return acc;
  }, {});

  // Calculate subject averages including missing assignments as 0
  const subjectStats = Object.keys(assignmentsBySubject).map(subjectId => {
    const assignments = assignmentsBySubject[subjectId];
    const subject = subjects.find(s => String(s.id) === String(subjectId));
    
    let totalPoints = 0;
    let earnedPoints = 0;
    const works = [];
    
    assignments.forEach(assignment => {
      totalPoints += assignment.points_possible || 0;
      
      // Check if this assignment has been completed/graded
      // completedWork.id is the assignment's _id
      const completedAssignment = completedWork.find(cw => String(cw.id) === String(assignment.id));
      
      if (completedAssignment) {
        earnedPoints += completedAssignment.points_awarded || 0;
        works.push(completedAssignment);
      } else {
        // Assignment is due but not submitted - counts as 0
        earnedPoints += 0;
      }
    });
    
    // Calculate weighted average based on total points
    const avgPercentage = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
    
    return {
      subject,
      avgPercentage,
      totalPoints,
      earnedPoints,
      assignmentCount: assignments.length,
      completedCount: works.length,
      works
    };
  }).filter(stat => stat.subject); // Filter out subjects that weren't found

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-xl text-slate-600">Loading...</div>
      </div>
    );
  }

  const child = children.find(c => c.id === selectedChild);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Grades & Progress</h1>
          <p className="text-slate-600 text-lg">Track academic performance and achievements</p>
        </div>

        {/* Child Selector and Generate Report Button */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex gap-3">
            {children.map(childItem => (
              <button
                key={childItem.id}
                onClick={() => setSelectedChild(childItem.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  selectedChild === childItem.id
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-slate-700 border-2 border-slate-200 hover:border-blue-300'
                }`}
              >
                {childItem.name}
              </button>
            ))}
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => window.open(`/progress-report?child=${selectedChild}`, '_blank')}
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Progress Report
            </Button>
            <Button
              onClick={() => window.open(`/report-card?child=${selectedChild}`, '_blank')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Report Card
            </Button>
            <Button
              onClick={() => window.open(`/standards-report?child=${selectedChild}`, '_blank')}
              className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-6 py-3"
              data-testid="generate-standards-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Standards Report
            </Button>
          </div>
        </div>

        {child && (
          <>
            {/* Subject Grade Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {subjectStats.map((stat, index) => {
                const colors = [
                  'from-blue-500 to-blue-600',
                  'from-green-500 to-green-600',
                  'from-purple-500 to-purple-600',
                  'from-orange-500 to-orange-600',
                  'from-pink-500 to-pink-600',
                  'from-teal-500 to-teal-600',
                  'from-indigo-500 to-indigo-600',
                  'from-red-500 to-red-600',
                ];
                const colorClass = colors[index % colors.length];
                const isSelected = selectedSubjectId === stat.subject?.id;

                return (
                  <Card 
                    key={stat.subject?.id || index}
                    onClick={() => setSelectedSubjectId(isSelected ? null : stat.subject?.id)}
                    className={`p-6 border-0 shadow-lg bg-gradient-to-br ${colorClass} text-white cursor-pointer transform transition-all duration-200 ${isSelected ? 'scale-105 ring-4 ring-white' : 'hover:scale-105'}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-3 bg-white/20 rounded-lg text-2xl">
                        {stat.subject?.icon || '📚'}
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium opacity-90 block">{stat.subject?.name || 'Unknown'}</span>
                        <span className="text-xs opacity-75">{stat.completedCount} / {stat.assignmentCount} completed</span>
                      </div>
                    </div>
                    <p className="text-4xl font-bold">{Math.round(stat.avgPercentage)}%</p>
                    <p className="text-sm opacity-90 mt-2">{stat.earnedPoints} / {stat.totalPoints} points</p>
                  </Card>
                );
              })}
            </div>

            {/* No Grades Message */}
            {subjectStats.length === 0 && (
              <Card className="p-12 text-center border-0 shadow-lg">
                <GraduationCap className="w-24 h-24 text-slate-300 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-slate-900 mb-4">No Assignments Due Yet</h2>
                <p className="text-slate-600">Grades will appear here once assignments are due.</p>
              </Card>
            )}

            {/* Assignment List for Selected Subject */}
            {selectedSubjectId && (
              <Card className="p-6 border-0 shadow-lg bg-white mb-8">
                {(() => {
                  const selectedStat = subjectStats.find(s => s.subject?.id === selectedSubjectId);
                  if (!selectedStat) return null;

                  return (
                    <>
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                          <div 
                            className="p-3 rounded-lg text-3xl"
                            style={{ backgroundColor: `${selectedStat.subject.color}20` }}
                          >
                            {selectedStat.subject.icon}
                          </div>
                          <div>
                            <h2 className="text-3xl font-bold text-slate-900">
                              {selectedStat.subject.name}
                            </h2>
                            <p className="text-slate-600">{selectedStat.completedCount} / {selectedStat.assignmentCount} completed</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={`text-2xl px-6 py-3 ${getGradeColor(selectedStat.avgPercentage)}`}>
                            {Math.round(selectedStat.avgPercentage)}%
                          </Badge>
                          <p className="text-slate-600 mt-2">
                            {Math.round(selectedStat.earnedPoints)}/{Math.round(selectedStat.totalPoints)} points
                          </p>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-6">
                        <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                          <span>Overall Progress</span>
                          <span>{Math.round(selectedStat.avgPercentage)}%</span>
                        </div>
                        <Progress value={selectedStat.avgPercentage} className="h-3" />
                      </div>

                      {/* Assignment List */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-slate-900 text-sm uppercase tracking-wide">Assignments</h4>
                        {selectedStat.works.map((work) => (
                        <div key={work.id} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                          {/* Assignment Header - Clickable */}
                          <div 
                            className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => handleExpandWork(work)}
                          >
                            <div className="flex-1 flex items-center gap-3">
                              <div>
                                {expandedWork?.id === work.id ? (
                                  <ChevronUp className="w-5 h-5 text-slate-600" />
                                ) : (
                                  <ChevronDown className="w-5 h-5 text-slate-600" />
                                )}
                              </div>
                              <div>
                                <h5 className="font-medium text-slate-900">
                                  {work.title}
                                  {work.points_awarded > work.points_possible && (
                                    <span className="ml-2 text-xs text-green-600 font-semibold">+BONUS</span>
                                  )}
                                </h5>
                                <p className="text-sm text-slate-600">
                                  Completed: {new Date(work.completed_date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge className={`${getGradeColor(work.percentage)} flex items-center justify-between gap-4 min-w-[200px]`}>
                                <span>{work.points_awarded}/{work.points_possible} ({Math.round(work.percentage)}%)</span>
                                <span>{work.letter_grade}</span>
                              </Badge>
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {expandedWork?.id === work.id && (
                            <div className="p-4 border-t border-slate-200 space-y-4">
                              {/* Assignment Description */}
                              {work.description && (
                                <div>
                                  <Label className="text-sm font-semibold text-slate-700">Assignment Description</Label>
                                  <p className="mt-1 text-slate-600">{work.description}</p>
                                </div>
                              )}

                              {/* Submission Info */}
                              {work.submission_description && (
                                <div className="bg-blue-50 p-3 rounded-lg">
                                  <Label className="text-sm font-semibold text-blue-900">Student's Submission Notes</Label>
                                  <p className="mt-1 text-sm text-blue-800">{work.submission_description}</p>
                                </div>
                              )}

                              {/* Teacher Notes */}
                              {work.notes && (
                                <div className="bg-amber-50 p-3 rounded-lg">
                                  <Label className="text-sm font-semibold text-amber-900">Teacher Notes</Label>
                                  <p className="mt-1 text-sm text-amber-800">{work.notes}</p>
                                </div>
                              )}

                              {/* Attachments */}
                              {work.attachments && work.attachments.length > 0 && (
                                <div>
                                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Attachments ({work.attachments.length})</Label>
                                  <div className="space-y-2">
                                    {work.attachments.map((attachment, idx) => (
                                      <div key={idx} className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200">
                                        {attachment.type === 'image' && attachment.url ? (
                                          <div className="space-y-2">
                                            <div className="aspect-video bg-slate-100 relative">
                                              <img 
                                                src={attachment.url} 
                                                alt={attachment.name}
                                                className="w-full h-full object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                                onClick={() => window.open(attachment.url, '_blank')}
                                              />
                                            </div>
                                            <div className="p-2 flex items-center justify-between">
                                              <div className="flex items-center gap-2">
                                                <ImageIcon className="w-4 h-4 text-blue-500" />
                                                <span className="text-sm text-slate-700">{attachment.name}</span>
                                              </div>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => window.open(attachment.url, '_blank')}
                                                className="text-blue-600 hover:text-blue-700"
                                              >
                                                View
                                              </Button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="p-2 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              <FileText className="w-4 h-4 text-slate-500" />
                                              <span className="text-sm text-slate-700">{attachment.name}</span>
                                            </div>
                                            {attachment.url && (
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => window.open(attachment.url, '_blank')}
                                                className="text-blue-600 hover:text-blue-700"
                                              >
                                                Download
                                              </Button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Action Buttons */}
                              <div className="flex gap-3 pt-2">
                                <Button
                                  onClick={() => handleEditGrade(work)}
                                  variant="outline"
                                  className="flex-1"
                                >
                                  <Edit2 className="w-4 h-4 mr-2" />
                                  Edit Grade
                                </Button>
                                <Button
                                  onClick={() => handleSendBack(work)}
                                  variant="outline"
                                  className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Send Back
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    </>
                  );
                })()}
              </Card>
            )}
          </>
        )}

        {/* Edit Grade Dialog */}
        <Dialog open={isEditingGrade} onOpenChange={setIsEditingGrade}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">Edit Grade</DialogTitle>
            </DialogHeader>
            {expandedWork && (
              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="font-bold text-lg text-slate-900">{expandedWork.title}</h4>
                  <p className="text-slate-600">Student: {child?.name}</p>
                </div>

                <div className="bg-slate-100 p-4 rounded-lg">
                  <Label className="text-lg font-semibold mb-3 block">Points</Label>
                  <div className="flex items-center gap-2 text-lg">
                    <Input
                      type="number"
                      placeholder="0"
                      value={editGradeData.pointsAwarded}
                      onChange={(e) => setEditGradeData({ ...editGradeData, pointsAwarded: e.target.value })}
                      className="w-24 text-center text-lg font-bold"
                      min="0"
                      step="0.5"
                    />
                    <span className="text-slate-600 font-medium">/ {expandedWork.points_possible}</span>
                    {editGradeData.pointsAwarded && (
                      <>
                        <Badge className="ml-2 bg-blue-100 text-blue-700">
                          {Math.round((parseFloat(editGradeData.pointsAwarded) / expandedWork.points_possible) * 100)}%
                        </Badge>
                        {parseFloat(editGradeData.pointsAwarded) > expandedWork.points_possible && (
                          <Badge className="bg-green-100 text-green-700 border-green-300">
                            +{(parseFloat(editGradeData.pointsAwarded) - expandedWork.points_possible).toFixed(1)} Bonus
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                  {editGradeData.pointsAwarded && parseFloat(editGradeData.pointsAwarded) > expandedWork.points_possible && (
                    <p className="text-sm text-green-600 mt-2">
                      Bonus points will count towards subject average
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="notes">Teacher Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Great improvement! Keep it up..."
                    value={editGradeData.notes}
                    onChange={(e) => setEditGradeData({ ...editGradeData, notes: e.target.value })}
                    className="mt-2 min-h-[100px]"
                  />
                </div>

                <div>
                  <Label htmlFor="tokens">Tokens to Award</Label>
                  <Input
                    id="tokens"
                    type="number"
                    min="0"
                    value={editGradeData.tokens}
                    onChange={(e) => setEditGradeData({ ...editGradeData, tokens: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsEditingGrade(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUpdateGrade}
                    disabled={!editGradeData.pointsAwarded || parseFloat(editGradeData.pointsAwarded) < 0}
                    className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white"
                  >
                    Update Grade
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Send Back Dialog */}
        <Dialog open={isSendingBack} onOpenChange={setIsSendingBack}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">Send Back for Resubmission</DialogTitle>
            </DialogHeader>
            {expandedWork && (
              <div className="space-y-6 mt-4">
                <div>
                  <h4 className="font-bold text-lg text-slate-900">{expandedWork.title}</h4>
                  <p className="text-slate-600">Student: {child?.name}</p>
                </div>

                <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
                  <p className="text-sm text-orange-800">
                    This will remove the current grade and send the assignment back to the student for resubmission. 
                    The student will be able to see your notes and submit updated work.
                  </p>
                </div>

                <div>
                  <Label htmlFor="sendBackNotes">Notes to Student (Optional)</Label>
                  <Textarea
                    id="sendBackNotes"
                    placeholder="Please review the instructions and resubmit..."
                    value={sendBackNotes}
                    onChange={(e) => setSendBackNotes(e.target.value)}
                    className="mt-2 min-h-[120px]"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setIsSendingBack(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendBackConfirm}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Send Back
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ParentGradesPage;
